(() => {
  if (customElements.get('inventa-article-content')) return;
  class InventaArticleContent extends HTMLElement {
    connectedCallback() {
      if (this._mounted) return;
      this._body = this.querySelector('[data-editorial-body]');
      if (!this._body) return;
      this._mounted = true;
      this._generation = (this._generation || 0) + 1;
      this._attributes = [];
      this._generated = [];
      const headings = [...this._body.querySelectorAll('h2,h3,h4')].filter(node => node.textContent.trim());
      if (headings.length >= 6) this.mountTOC(headings);
      this.mountTables();
    }
    remember(node, name, value) {
      this._attributes.push([node, name, node.getAttribute(name)]);
      node.setAttribute(name, value);
    }
    mountTOC(headings) {
      const details = document.createElement('details');
      details.className = 'inventa-insights-toc';
      const summary = document.createElement('summary');
      summary.textContent = this.dataset.tocLabel;
      const nav = document.createElement('nav');
      nav.setAttribute('aria-label', this.dataset.tocNavigation);
      const root = document.createElement('ul');
      let stack = [{level: 1, list: root, item: null}];
      const targets = new Map();
      let legacyH2Index = 0;
      headings.forEach((heading, index) => {
        if (heading.tagName === 'H2') legacyH2Index += 1;
        if (!heading.id || document.getElementById(heading.id) !== heading) {
          const stem = heading.tagName === 'H2' ? `insights-section-${legacyH2Index}` : `article-${this.dataset.articleId || 'content'}-section-${index + 1}`;
          let id = stem;
          let suffix = 2;
          while (document.getElementById(id)) id = `${stem}-${suffix++}`;
          this.remember(heading, 'id', id);
        }
        if (!heading.hasAttribute('tabindex')) this.remember(heading, 'tabindex', '-1');
        const level = Number(heading.tagName.substring(1));
        while (stack.length > 1 && level <= stack.at(-1).level) stack.pop();
        const parent = stack.at(-1);
        if (parent.item && level > Number(parent.item.dataset.headingLevel)) {
          const nested = document.createElement('ul');
          parent.item.append(nested);
          stack.push({level: Number(parent.item.dataset.headingLevel), list: nested, item: null});
        }
        const active = stack.at(-1);
        const item = document.createElement('li');
        item.dataset.headingLevel = String(level);
        const link = document.createElement('a');
        link.setAttribute('href', `#${encodeURIComponent(heading.id)}`);
        link.textContent = heading.textContent.trim();
        targets.set(link, heading);
        item.append(link);
        active.list.append(item);
        active.item = item;
      });
      this._onTOCClick = event => {
        const link = event.target.closest('a');
        const target = targets.get(link);
        if (!target || event.defaultPrevented || event.button > 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const generation = this._generation;
        queueMicrotask(() => {
          if (this.isConnected && generation === this._generation) target.focus({preventScroll: true});
        });
      };
      nav.addEventListener('click', this._onTOCClick);
      this._tocNav = nav;
      nav.append(root);
      details.append(summary, nav);
      this._body.before(details);
      this._generated.push(details);
    }
    mountTables() {
      const tables = [...this._body.querySelectorAll('table')].map((table, index) => {
        const hint = document.createElement('p');
        hint.className = 'inventa-insights-table-hint inventa-type-help';
        hint.textContent = this.dataset.tableHint;
        table.before(hint);
        this._generated.push(hint);
        if (!table.hasAttribute('aria-label') && !table.querySelector('caption')) this.remember(table, 'aria-label', `${this.dataset.tableLabel} ${index + 1}`);
        const originalTabindex = table.getAttribute('tabindex');
        this._attributes.push([table, 'tabindex', originalTabindex]);
        return {table, hint, originalTabindex};
      });
      const update = () => {
        for (const {table, hint, originalTabindex} of tables) {
          const overflow = table.scrollWidth > table.clientWidth + 1;
          hint.hidden = !overflow;
          if (overflow && originalTabindex === null) table.setAttribute('tabindex', '0');
          else if (originalTabindex === null) table.removeAttribute('tabindex');
          else table.setAttribute('tabindex', originalTabindex);
        }
      };
      if (tables.length && typeof ResizeObserver !== 'undefined') {
        this._observer = new ResizeObserver(update);
        tables.forEach(({table}) => this._observer.observe(table));
      }
      update();
    }
    disconnectedCallback() {
      this._generation = (this._generation || 0) + 1;
      this._observer?.disconnect();
      this._observer = null;
      this._tocNav?.removeEventListener('click', this._onTOCClick);
      this._tocNav = null;
      this._generated?.forEach(node => node.remove());
      this._attributes?.reverse().forEach(([node, name, value]) => {
        if (value === null) node.removeAttribute(name);
        else node.setAttribute(name, value);
      });
      this._mounted = false;
    }
  }
  customElements.define('inventa-article-content', InventaArticleContent);
})();
